import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { UiButton, UiIcon } from 'ui-components';
import { sendRatedFeedback } from 'store';

import {
  StyledButtonWrapper,
  StyledContainer,
  StyledDescription,
  StyledRateItem,
  StyledRateItems,
  StyledThanks,
  StyledTitle,
} from './RatedFeedback.styles';

const RatedFeedback = ({
  title,
  description,
  rateItems = [1, 2, 3, 4, 5],
  params,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedItem, setSelectedItem] = useState(null);
  const [show, setShow] = useState(false);
  const [observed, setObserved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('rate');
  const targetRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setObserved(true);
          }
        });
      },
      { threshold: 0 }
    );

    if (targetRef.current) {
      observer.observe(targetRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const ratedFeedback =
      JSON.parse(localStorage.getItem('rated_feedback')) || {};

    if (
      !ratedFeedback[params.type] ||
      !ratedFeedback[params.type]?.includes(params.id)
    ) {
      setShow(true);
      setObserved(false);
      setSelectedItem(null);
      setStep('rate');
    } else {
      setShow(false);
    }
  }, [params.id]);

  const handleClick = (item) => {
    setSelectedItem(item);
  };

  const handleClose = () => {
    setShow(false);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await dispatch(
        sendRatedFeedback({ ...params, rate: selectedItem })
      ).unwrap();
      setLoading(false);
      setStep('thanks');
      setTimeout(handleClose, 3000);

      const ratedFeedback =
        JSON.parse(localStorage.getItem('rated_feedback')) || {};

      if (!ratedFeedback[params.type]) {
        ratedFeedback[params.type] = [];
      }
      ratedFeedback[params.type].push(params.id);

      localStorage.setItem('rated_feedback', JSON.stringify(ratedFeedback));
    } catch (error) {
      setLoading(false);
    }
  };

  const content = {
    rate: (
      <div className='rated-feedback'>
        <UiIcon
          icon='ri-close-line'
          className='close-button'
          size={20}
          onClick={handleClose}
        />
        <StyledTitle>{title}</StyledTitle>
        <StyledDescription>{description}</StyledDescription>
        <StyledRateItems>
          {rateItems.map((item) => (
            <StyledRateItem
              active={selectedItem === item}
              onClick={() => handleClick(item)}
            >
              {item}
            </StyledRateItem>
          ))}
        </StyledRateItems>
        {selectedItem && (
          <StyledButtonWrapper>
            <UiButton
              disabled={loading}
              loading={loading}
              variant='text'
              onClick={handleSubmit}
            >
              {t('common:addRatedFeedback')}
            </UiButton>
          </StyledButtonWrapper>
        )}
      </div>
    ),
    thanks: (
      <div className='rated-feedback'>
        <StyledThanks>
          <span className='circle outer-circle'>
            <span className='circle inner-circle'>
              <UiIcon icon='ri-heart-3-line' className='heart-icon' size={28} />
            </span>
          </span>
          <p className='success-message'>
            {t('common:success.ratedFeedback.description')}
          </p>
        </StyledThanks>
      </div>
    ),
  };

  return (
    <StyledContainer ref={targetRef} fixed={observed}>
      {show && content[step]}
    </StyledContainer>
  );
};

RatedFeedback.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  rateItems: PropTypes.array,
  params: PropTypes.object,
};

export default RatedFeedback;
